import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { PAGE_SIZE_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import { PrettySelectComponent } from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { InvoicesService } from '../../../shared/services/invoices.service';
import { INVOICE_STATUS_META, Invoice } from '../../../shared/types/invoice.types';
import { TPipe } from '../../../shared/pipe/t.pipe';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';

/**
 * Liste self-service des factures du user courant. Endpoint `/api/invoices/me`
 * — paginé côté back depuis le patch v2 (enveloppe Nawam ApiCollection,
 * 1-indexé). Pas de filtre statut/RSQL côté front : la collection est petite
 * et le besoin owner se limite à la consultation.
 *
 * Deux onglets : toutes / en retard (`/me/overdue`).
 */
@Component({
  selector: 'app-invoices-list',
  imports: [CommonModule, FormsModule, RouterModule, PageHeaderComponent, DataStateComponent, PrettySelectComponent, TPipe, LocalizedDatePipe],
  templateUrl: './invoices-list.component.html',
})
export class InvoicesListComponent implements OnInit {
  private readonly invoicesService = inject(InvoicesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly invoices = signal<Invoice[]>([]);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly page = signal(1);
  readonly size = signal(10);
  readonly tab = signal<'all' | 'overdue'>('all');

  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly statusMeta = INVOICE_STATUS_META;

  readonly pageWindow = computed<number[]>(() => {
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.page();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void {
    this.load();
  }

  switchTab(next: 'all' | 'overdue'): void {
    if (next === this.tab()) return;
    this.tab.set(next);
    this.page.set(1);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const obs =
      this.tab() === 'overdue'
        ? this.invoicesService.listMineOverdue({ page: this.page(), size: this.size() })
        : this.invoicesService.listMine({ page: this.page(), size: this.size() });

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.invoices.set(res.data ?? []);
        this.totalItems.set(res.totalItems ?? 0);
        this.totalPages.set(res.totalPages ?? 0);
        this.loading.set(false);
      },
      error: (err: unknown) => {
        this.loading.set(false);
        this.invoices.set([]);
        this.totalItems.set(0);
        this.totalPages.set(0);
        this.error.set(toErrorMessage(err));
      },
    });
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages() || p === this.page()) return;
    this.page.set(p);
    this.load();
  }

  changeSize(s: number): void {
    if (!s || s === this.size()) return;
    this.size.set(s);
    this.page.set(1);
    this.load();
  }
}
